import { catalogBlendDisplayName, blendKeyFromAnswers, planPreviewRequest, sanitizeBlendKey } from './plantingPlanDisplay';
import { buildConfirmedGroundPlace } from './plantingGroundUtils';
import { hydrateWizardAnswers } from './plantingWizardUtils';

export const WEATHER_MAINS = Object.freeze([
  'Clear',
  'Clouds',
  'Rain',
  'Drizzle',
  'Thunderstorm',
  'Snow',
  'Mist',
  'Fog',
]);

const WEATHER_STRING_KEYS = ['main', 'description', 'icon', 'source'];
const WEATHER_NUMBER_KEYS = [
  'temperature',
  'humidity',
  'feels_like',
  'wind_speed',
  'wind_direction',
  'precip_mm',
];

function pad2(value) {
  return String(value).padStart(2, '0');
}

export function localTodayIso(now = new Date()) {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

export function isPlantingDateToday(dateStr, now = new Date()) {
  return String(dateStr || '') === localTodayIso(now);
}

function finitePair(lat, lng) {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

export function resolvePlanCoords(group) {
  if (!group) return null;
  return finitePair(group.place_latitude, group.place_longitude)
    || finitePair(group.centroid_lat, group.centroid_lng);
}

export function shouldAutoFetchWeather({ plantedAt, coords, now } = {}) {
  return Boolean(isPlantingDateToday(plantedAt, now) && coords);
}

export function placeFromSavedGroup(group) {
  if (!group) return null;
  return buildConfirmedGroundPlace({
    place: {
      latitude: group.place_latitude,
      longitude: group.place_longitude,
      label: group.place_label || '',
    },
    zoneCode: group.usda_zone_code,
    soilTypeId: group.soil_type,
    soilTypeLabel: group.soil_type,
    drainageLabel: group.soil_drainage,
    drainageRaw: group.soil_drainage,
    soilPh: group.soil_ph,
    frostLastLabel: group.last_frost_label,
    frostFirstLabel: group.first_frost_label,
    soilSurveyCovered: Boolean(group.soil_type || group.soil_drainage),
  });
}

export async function resolvePlanBlendName(group, loadPlan) {
  const key = sanitizeBlendKey(group?.wizard_answers?.blend);
  if (key) return catalogBlendDisplayName(key);
  const answers = hydrateWizardAnswers(group?.wizard_answers, group?.planting_season);
  if (typeof loadPlan === 'function') {
    try {
      const preview = await loadPlan(planPreviewRequest({
        answers,
        place: placeFromSavedGroup(group),
        acres: Number(group?.manual_acres || group?.total_acres) || 0,
        blend: '',
        draftSoilPh: group?.soil_ph,
      }));
      if (preview?.blend?.name) return String(preview.blend.name).trim();
    } catch (err) {
      console.error('Plan preview for blend name failed', err);
    }
  }
  return catalogBlendDisplayName(blendKeyFromAnswers(answers, ''));
}

export function emptyPlantingWeather() {
  return {
    main: '',
    description: '',
    temperature: '',
    humidity: '',
    wind_speed: '',
    wind_direction: '',
    precip_mm: '',
    source: 'manual',
  };
}

export function weatherFromCurrent(current) {
  if (!current || typeof current !== 'object') return emptyPlantingWeather();
  return {
    main: current.main || '',
    description: current.description || '',
    temperature: current.temperature ?? '',
    humidity: current.humidity ?? '',
    wind_speed: current.wind_speed ?? '',
    wind_direction: current.wind_direction ?? '',
    precip_mm: current.precip_mm ?? current.rain_1h ?? '',
    icon: current.icon || '',
    source: 'auto',
  };
}

export function markWeatherManual(weather) {
  return { ...(weather || emptyPlantingWeather()), source: 'manual' };
}

export function buildWeatherPayload(weather) {
  if (!weather || typeof weather !== 'object') return {};
  const out = {};
  WEATHER_STRING_KEYS.forEach((key) => {
    const value = String(weather[key] ?? '').trim();
    if (value) out[key] = value;
  });
  WEATHER_NUMBER_KEYS.forEach((key) => {
    const raw = weather[key];
    if (raw === '' || raw === null || raw === undefined) return;
    const value = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(value)) out[key] = value;
  });
  const hasObservation = Object.keys(out).some((key) => key !== 'source');
  if (!hasObservation) return {};
  if (!out.source) out.source = 'manual';
  return out;
}

export async function buildPlantingEventPayload(group, { plantedAt, notes, weather, loadPlan } = {}) {
  return {
    area_group: group?.id ?? null,
    planted_at: plantedAt,
    notes: notes || '',
    blend_name: await resolvePlanBlendName(group, loadPlan),
    weather_data: buildWeatherPayload(weather),
  };
}
